import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, GripVertical } from 'lucide-react';

/**
 * DraggableFrameGrid — multi-row grid with:
 *  - Left/right half drop detection: cards shift to "make room"
 *  - (+) buttons centered in the gap between cards
 *  - Grip handle on each card
 */
export default function DraggableFrameGrid({ items, gridClass, renderCard, onReorder, onInsertAfter }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [dropPos, setDropPos] = useState(null);
  const [cardRects, setCardRects] = useState([]);
  const gridRef = useRef(null);
  const dragRef = useRef(null);

  const isDragging = dragIdx !== null;

  // Measure card positions after render to place (+) buttons in gaps
  useEffect(() => {
    if (!gridRef.current || !items.length) return;
    const cards = gridRef.current.querySelectorAll('[data-role="grid-card"]');
    const gridRect = gridRef.current.getBoundingClientRect();
    const rects = Array.from(cards).map(c => {
      const r = c.getBoundingClientRect();
      return { left: r.left - gridRect.left, top: r.top - gridRect.top, width: r.width, height: r.height, right: r.right - gridRect.left, bottom: r.bottom - gridRect.top };
    });
    setCardRects(rects);
  }, [items, items.length]);

  const handleDragStart = useCallback((e, idx) => {
    setDragIdx(idx);
    dragRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDropPos(null);
  }, []);

  const handleCardDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragRef.current === null) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const side = x < rect.width / 2 ? 'before' : 'after';
    const insertIdx = side === 'before' ? idx : idx + 1;
    const fromIdx = dragRef.current;

    if (insertIdx === fromIdx || insertIdx === fromIdx + 1) {
      setDropPos(null);
      return;
    }
    setDropPos({ index: idx, side });
  }, []);

  const handleCardDrop = useCallback((e) => {
    e.preventDefault();
    const fromIdx = dragRef.current;
    if (fromIdx === null || !dropPos) {
      setDragIdx(null);
      setDropPos(null);
      return;
    }

    const insertIdx = dropPos.side === 'before' ? dropPos.index : dropPos.index + 1;
    if (insertIdx === fromIdx || insertIdx === fromIdx + 1) {
      setDragIdx(null);
      setDropPos(null);
      return;
    }

    const newItems = [...items];
    const [moved] = newItems.splice(fromIdx, 1);
    const adjustedIdx = insertIdx > fromIdx ? insertIdx - 1 : insertIdx;
    newItems.splice(adjustedIdx, 0, moved);

    setDragIdx(null);
    setDropPos(null);
    onReorder(newItems.map(i => i.id));
  }, [items, onReorder, dropPos]);

  // Compute (+) button positions: center of the gap between adjacent cards.
  //
  // STALE-RENDER GUARD: cardRects is populated by a useEffect that runs AFTER
  // commit, so during the render immediately following an items mutation
  // (e.g. bulk-delete), cardRects still holds the OLD array. If we walked it
  // here, `items[i - 1]` would be undefined for any index past items.length-1
  // and crash the whole tree (no ErrorBoundary → blank black page).
  // The next render after useEffect re-fires will draw the gap buttons.
  const gapButtons = [];
  const inSync = cardRects.length === items.length;
  if (inSync && cardRects.length > 0 && !isDragging) {
    for (let i = 0; i <= cardRects.length; i++) {
      // Double-belt-and-suspenders: skip any stale index defensively.
      if (i > 0 && !items[i - 1]) continue;
      let x, y, h, isVertical = false;
      if (i === 0) {
        // Before first card
        x = cardRects[0].left - 10;
        y = cardRects[0].top;
        h = cardRects[0].height;
      } else if (i === cardRects.length) {
        // After last card — below it
        const last = cardRects[i - 1];
        x = last.left + (last.width / 2) - 10;
        y = last.bottom + 2;
        h = 24;
        isVertical = true;
      } else {
        const prev = cardRects[i - 1];
        const curr = cardRects[i];
        // Same row? Position in the horizontal gap
        if (Math.abs(prev.top - curr.top) < 20) {
          x = prev.right + (curr.left - prev.right) / 2 - 10;
          y = curr.top;
          h = curr.height;
        } else {
          // Different rows: center between bottom of prev and top of next
          isVertical = true;
          x = prev.left + (prev.width / 2) - 10;
          y = prev.bottom + ((curr.top - prev.bottom) / 2) - 12;
          h = 24;
        }
      }

      gapButtons.push(
        <button
          key={`gap-${i}`}
          data-testid={i === 0 ? 'insert-shot-start' : `insert-shot-after-${items[i - 1].id}`}
          onClick={() => onInsertAfter(i === 0 ? null : items[i - 1].id)}
          className={`absolute z-30 flex items-center justify-center rounded-full opacity-40 hover:opacity-100 text-white/40 hover:text-[#22C55E] hover:bg-[#22C55E]/15 transition-all duration-200 hover:scale-110 ${
            isVertical ? 'w-8 h-6' : 'w-5 h-12'
          }`}
          style={{
            left: `${x}px`,
            top: isVertical ? `${y}px` : `${y + (h / 2) - 24}px`,
          }}
        >
          <Plus className="w-4 h-4" />
        </button>
      );
    }
  }

  if (!items || items.length === 0) return null;

  return (
    <div className="mt-3 relative" data-testid="draggable-frame-grid" ref={gridRef}>
      <div className={`grid ${gridClass} gap-3`}>
        {items.map((item, idx) => {
          const isBeingDragged = dragIdx === idx;
          const showLeft = dropPos?.index === idx && dropPos?.side === 'before';
          const showRight = dropPos?.index === idx && dropPos?.side === 'after';

          return (
            <div
              key={item.id}
              data-role="grid-card"
              className="relative group/cell"
            >
              {/* Drop indicator line — LEFT */}
              {showLeft && (
                <div className="absolute -left-[7px] top-0 bottom-12 w-[3px] rounded-full bg-[#22C55E] shadow-[0_0_12px_rgba(34,197,94,0.6)] z-20" />
              )}
              {/* Drop indicator line — RIGHT */}
              {showRight && (
                <div className="absolute -right-[7px] top-0 bottom-12 w-[3px] rounded-full bg-[#22C55E] shadow-[0_0_12px_rgba(34,197,94,0.6)] z-20" />
              )}

              <div
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleCardDragOver(e, idx)}
                onDrop={(e) => handleCardDrop(e)}
                data-testid={`draggable-card-${item.id}`}
                className={`relative transition-all duration-300 ease-out ${
                  isBeingDragged ? 'opacity-20 scale-[0.95]' : ''
                } ${showLeft ? 'translate-x-3' : ''} ${showRight ? '-translate-x-3' : ''}`}
              >
                {/* Drag handle */}
                <div
                  data-testid={`drag-handle-${item.id}`}
                  className={`absolute top-1.5 left-1.5 z-10 p-1 rounded-md backdrop-blur-sm cursor-grab active:cursor-grabbing transition-opacity duration-200 ${
                    isDragging ? 'opacity-90 bg-black/60' : 'opacity-0 group-hover/cell:opacity-100 bg-black/40'
                  }`}
                >
                  <GripVertical className="w-3.5 h-3.5 text-white/80" />
                </div>

                {renderCard(item, idx)}
              </div>
            </div>
          );
        })}
      </div>

      {/* (+) buttons overlaid in the gaps between cards */}
      {gapButtons}
    </div>
  );
}
